'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ROIData {
  month: string;
  investment: number;
  returns: number;
  netGain: number;
}

interface ROISectionProps {
  roiData: ROIData[];
  totalInvestment: string;
  totalReturns: string;
  roiPercentage: string;
  paybackPeriod: string;
}

const ROISection = ({ roiData, totalInvestment, totalReturns, roiPercentage, paybackPeriod }: ROISectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
                ROI Analyse
              </h2>
            </div>
            <div className="glassmorphic rounded-xl p-8 h-96 animate-pulse"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              ROI Analyse
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Gedetailleerde breakdown van investering versus rendement over tijd
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="glassmorphic rounded-xl p-6 text-center shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow">
              <Icon name="CurrencyEuroIcon" size={32} className="text-primary mx-auto mb-4" />
              <div className="text-3xl font-display font-bold text-foreground mb-2">
                {totalInvestment}
              </div>
              <div className="text-sm text-muted-foreground">Totale Investering</div>
            </div>
            
            <div className="glassmorphic rounded-xl p-6 text-center shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow">
              <Icon name="ChartBarIcon" size={32} className="text-success mx-auto mb-4" />
              <div className="text-3xl font-display font-bold text-success mb-2 glow-success">
                {totalReturns}
              </div>
              <div className="text-sm text-muted-foreground">Totaal Rendement</div>
            </div>
            
            <div className="glassmorphic rounded-xl p-6 text-center shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow">
              <Icon name="ArrowTrendingUpIcon" size={32} className="text-accent mx-auto mb-4" />
              <div className="text-3xl font-display font-bold text-success mb-2 glow-success">
                {roiPercentage}
              </div>
              <div className="text-sm text-muted-foreground">ROI Percentage</div>
            </div>
            
            <div className="glassmorphic rounded-xl p-6 text-center shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow">
              <Icon name="ClockIcon" size={32} className="text-secondary mx-auto mb-4" />
              <div className="text-3xl font-display font-bold text-foreground mb-2">
                {paybackPeriod}
              </div>
              <div className="text-sm text-muted-foreground">Terugverdientijd</div>
            </div>
          </div>
          
          <div className="glassmorphic rounded-xl p-8 shadow-silver-glow card-3d-glow">
            <h3 className="text-2xl font-display font-bold text-foreground mb-6 text-center">
              ROI Ontwikkeling Over Tijd
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={roiData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#ADB5BD"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#ADB5BD"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1A1A1A', 
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '8px',
                      color: '#F8F9FA'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="investment" 
                    stroke="#FF4757" 
                    strokeWidth={2}
                    name="Investering"
                    dot={{ fill: '#FF4757', r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="returns" 
                    stroke="#00FF88" 
                    strokeWidth={2}
                    name="Rendement"
                    dot={{ fill: '#00FF88', r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="netGain" 
                    stroke="#00D4FF" 
                    strokeWidth={3}
                    name="Netto Winst"
                    dot={{ fill: '#00D4FF', r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROISection;